/**
 * File Notes API — append-only progress log for each client file.
 *
 * Notes record both human-written entries (Adviser / Assistant) and
 * automatic system events (file drawn, paused, resumed, sent to sign,
 * completed). The log persists for the lifetime of the file and is
 * never edited or deleted (FAIS audit-trail requirement).
 */

import supabase from './supabaseClient';

export const NOTE_BODY_MAX = 200;

/**
 * The two roles allowed on a client file (CLAUDE.md §24 / §31). A human note
 * is acknowledged by the OTHER of these two roles. Roles are derived from the
 * active UI role at write time (they are never stored in the DB), so the
 * routing rule lives here rather than in a DB trigger.
 */
export const FILE_NOTE_ROLE_PAIR = ['adviser', 'assistant'];

/**
 * Given a note's author type and the role the author was acting as, decide
 * whether the note requires acknowledgement and, if so, which role must
 * acknowledge it. System notes never require acknowledgement, and a note whose
 * author role is not one of the file's two roles requires none either
 * (defensive fallback — CLAUDE.md §31).
 */
export function computeFileNoteAck(authorType, authorRole) {
  if (authorType === 'system' || !FILE_NOTE_ROLE_PAIR.includes(authorRole)) {
    return { requiresAck: false, acknowledgerRole: null };
  }
  return {
    requiresAck: true,
    acknowledgerRole: FILE_NOTE_ROLE_PAIR.find((r) => r !== authorRole),
  };
}

function mapRowToNote(row) {
  return {
    id: row.id,
    fileId: row.file_id,
    authorType: row.author_type,
    authorId: row.author_id || null,
    authorDisplayName: row.author_display_name || '',
    body: row.body || '',
    createdAt: row.created_at,
    // Acknowledgement status (CLAUDE.md §31)
    requiresAck: row.requires_ack === true,
    acknowledgerRole: row.acknowledger_role || null,
    acknowledgedAt: row.acknowledged_at || null,
    acknowledgedBy: row.acknowledged_by || null,
  };
}

/**
 * Fetch all notes for a single file, oldest first (journal order).
 */
export async function getNotesForFile(fileId) {
  const { data, error } = await supabase
    .from('file_notes')
    .select('*')
    .eq('file_id', fileId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(`Failed to fetch file notes: ${error.message}`);
  return (data || []).map(mapRowToNote);
}

/**
 * Fetch note counts for many files in one query.
 *
 * Returns { counts, pendingByRole } where:
 *   - counts[fileId]        = total number of notes on that file (drives the
 *                             existing "N notes" badge — unchanged).
 *   - pendingByRole[fileId] = { [acknowledgerRole]: n } — notes still awaiting
 *                             acknowledgement (requires_ack = true AND
 *                             acknowledged_at IS NULL), keyed by the role that
 *                             must acknowledge them. Card illumination is
 *                             OWNER-ONLY (CLAUDE.md §31): a card is lit for a
 *                             viewer ⇔ pendingByRole[fileId][activeRole] > 0.
 *                             Nobody else — the author included — sees any
 *                             illumination.
 */
export async function getNoteCountsByFile() {
  const { data, error } = await supabase
    .from('file_notes')
    .select('file_id, requires_ack, acknowledged_at, acknowledger_role');

  if (error) throw new Error(`Failed to fetch note counts: ${error.message}`);
  const counts = {};
  const pendingByRole = {};
  for (const row of data || []) {
    counts[row.file_id] = (counts[row.file_id] || 0) + 1;
    if (row.requires_ack === true && !row.acknowledged_at && row.acknowledger_role) {
      const byRole = pendingByRole[row.file_id] || (pendingByRole[row.file_id] = {});
      byRole[row.acknowledger_role] = (byRole[row.acknowledger_role] || 0) + 1;
    }
  }
  return { counts, pendingByRole };
}

/**
 * Fetch every file note still awaiting acknowledgement BY the given role,
 * oldest first, with the parent file's client name and status embedded (via
 * the file_id FK). Drives the login catch-up gate (CLAUDE.md §31.9).
 */
export async function getPendingNotesForRole(role) {
  if (!role) return [];
  const { data, error } = await supabase
    .from('file_notes')
    .select('*, files(client_name, status)')
    .eq('requires_ack', true)
    .is('acknowledged_at', null)
    .eq('acknowledger_role', role)
    .order('created_at', { ascending: true });

  if (error) throw new Error(`Failed to fetch pending notes: ${error.message}`);
  return (data || []).map((row) => ({
    ...mapRowToNote(row),
    clientName: row.files?.client_name || '',
    fileStatus: row.files?.status || null,
  }));
}

/**
 * Fetch every system-authored note across all files in one query and
 * group them by file_id, sorted ascending by created_at within each
 * group. Used by the dashboards to feed the bonus / SLA clock
 * (CLAUDE.md §21).
 */
export async function getAllSystemNotes() {
  const { data, error } = await supabase
    .from('file_notes')
    .select('*')
    .eq('author_type', 'system')
    .order('created_at', { ascending: true });

  if (error) throw new Error(`Failed to fetch system notes: ${error.message}`);
  const byFile = {};
  for (const row of data || []) {
    const note = mapRowToNote(row);
    if (!byFile[note.fileId]) byFile[note.fileId] = [];
    byFile[note.fileId].push(note);
  }
  return byFile;
}

/**
 * Insert a new note. Body is trimmed and length-checked client-side
 * (the DB also enforces a 200-char ceiling — migration 21).
 */
export async function createNote({
  fileId,
  authorType,
  authorId = null,
  authorDisplayName,
  body,
  authorRole = null,
}) {
  const trimmed = (body || '').trim();
  if (!trimmed) throw new Error('Note body is empty.');
  if (trimmed.length > NOTE_BODY_MAX) {
    throw new Error(`Note body exceeds ${NOTE_BODY_MAX} characters.`);
  }
  if (!authorType || (authorType !== 'user' && authorType !== 'system')) {
    throw new Error('authorType must be "user" or "system".');
  }
  if (!authorDisplayName) {
    throw new Error('authorDisplayName is required.');
  }

  // Acknowledgement routing (CLAUDE.md §31) is computed here — the one place
  // that knows the author's active role — not in a DB trigger (roles are not
  // stored in the DB; the author may be a multi-role user).
  const { requiresAck, acknowledgerRole } = computeFileNoteAck(authorType, authorRole);

  const { data, error } = await supabase
    .from('file_notes')
    .insert({
      file_id: fileId,
      author_type: authorType,
      author_id: authorId,
      author_display_name: authorDisplayName,
      body: trimmed,
      requires_ack: requiresAck,
      acknowledger_role: acknowledgerRole,
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create note: ${error.message}`);
  return mapRowToNote(data);
}

/**
 * Acknowledge a note (mark it as read by the responsible role). Routes through
 * the acknowledge_note() SECURITY DEFINER RPC — the only write path for the
 * acknowledgement columns, since file_notes has no UPDATE policy (append-only).
 * Idempotent: acknowledging an already-acknowledged note returns it unchanged.
 * Returns the updated note (camelCase).
 */
export async function acknowledgeNote(noteId) {
  const { data, error } = await supabase.rpc('acknowledge_note', { p_note_id: noteId });
  if (error) throw new Error(`Failed to acknowledge note: ${error.message}`);
  // A composite-returning RPC may come back as a single object or a 1-row array.
  const row = Array.isArray(data) ? data[0] : data;
  return row ? mapRowToNote(row) : null;
}

/**
 * Convenience helper: insert a system-authored note.
 */
export async function createSystemNote(fileId, body) {
  return createNote({
    fileId,
    authorType: 'system',
    authorId: null,
    authorDisplayName: 'System',
    body,
  });
}
